import { useCallback, useEffect, useState } from 'react';

import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Tab, Table, Tabs } from 'react-bootstrap';
import { FaCubes, FaPenToSquare, FaPlus, FaTrashCan } from 'react-icons/fa6';
import { useApp } from 'common/AppContext';

const blankApp = { id: null, name: '', context: '', sitemap: '', testrail_project_id: null };
const blankEnv = (appId = null) => ({ id: null, name: '', app_id: appId, url: '' });

export const AdminApps = () => {

    const { api } = useApp();

    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingApp, setEditingApp] = useState(null);
    const [editingEnv, setEditingEnv] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {

        setLoading(true);
        setError(null);

        return api.get('/api/apps')
            .then(d => setApps(d?.apps || []))
            .catch(e => setError(e?.err || e?.message || String(e)))
            .finally(() => setLoading(false));

    }, [api]);

    useEffect(() => {

        load();

    }, [load]);

    const saveApp = useCallback(async form => {

        setBusy(true);
        setError(null);

        try {

            const body = {
                name: form.name,
                context: form.context,
                sitemap: form.sitemap,
                testrail_project_id: form.testrail_project_id || null
            };
            const data = form.id
                ? await api.request('PATCH', `/api/apps/${form.id}`, body)
                : await api.post('/api/apps', body);

            setApps(prev => {

                const next = data.app;
                const idx = prev.findIndex(a => a.id === next.id);
                if (idx === -1) return [...prev, next].sort((a, b) => a.name.localeCompare(b.name));
                const out = prev.slice();

                // Preserve env list — server's response on PATCH doesn't include them
                out[idx] = { ...next, environments: prev[idx].environments };
                return out;

            });

            setEditingApp(null);

        } catch (e) {

            setError(e?.err || e?.message || String(e));

        } finally {

            setBusy(false);

        }

    }, [api]);

    const removeApp = useCallback(async app => {

        if (!window.confirm(`Delete app "${app.name}"? Its environments must be removed first.`)) return;

        setBusy(true);
        setError(null);

        try {

            await api.request('DELETE', `/api/apps/${app.id}`);
            setApps(prev => prev.filter(a => a.id !== app.id));

        } catch (e) {

            setError(e?.err || e?.message || String(e));

        } finally {

            setBusy(false);

        }

    }, [api]);

    const saveEnv = useCallback(async form => {

        setBusy(true);
        setError(null);

        try {

            const body = { name: form.name, app_id: form.app_id, url: form.url };
            const data = form.id
                ? await api.request('PATCH', `/api/environments/${form.id}`, body)
                : await api.post('/api/environments', body);

            const env = data.environment;
            setApps(prev => prev.map(a => {

                if (a.id !== env.app_id) {

                    // Env may have moved between apps — drop from old app
                    return { ...a, environments: (a.environments || []).filter(e => e.id !== env.id) };

                }
                const envs = (a.environments || []).slice();
                const idx = envs.findIndex(e => e.id === env.id);
                if (idx === -1) envs.push({ id: env.id, name: env.name, url: env.url });
                else envs[idx] = { id: env.id, name: env.name, url: env.url };
                envs.sort((x, y) => x.name.localeCompare(y.name));
                return { ...a, environments: envs };

            }));

            setEditingEnv(null);

        } catch (e) {

            setError(e?.err || e?.message || String(e));

        } finally {

            setBusy(false);

        }

    }, [api]);

    const removeEnv = useCallback(async (env, appId) => {

        if (!window.confirm(`Delete environment "${env.name}"?`)) return;

        setBusy(true);
        setError(null);

        try {

            await api.request('DELETE', `/api/environments/${env.id}`);
            setApps(prev => prev.map(a => (a.id === appId
                ? { ...a, environments: (a.environments || []).filter(e => e.id !== env.id) }
                : a)));

        } catch (e) {

            setError(e?.err || e?.message || String(e));

        } finally {

            setBusy(false);

        }

    }, [api]);

    return (

        <Container className="py-4">

            <div className="d-flex align-items-center justify-content-between mb-1">
                <div className="d-flex align-items-center gap-2">
                    <FaCubes className="text-primary" size={22} />
                    <h3 className="m-0">
                        Apps & Environments
                    </h3>
                </div>
                <Button onClick={() => setEditingApp({ ...blankApp })} variant="primary">
                    <FaPlus className="me-2" />
                    Add app
                </Button>
            </div>
            <p className="text-muted">
                Each app owns a context primer + sitemap that&apos;s appended to the LLM&apos;s system prompt
                whenever the runner targets one of its environments. Environments are thin URL pointers — same
                app, different deployment.
            </p>

            {error && (
                <Alert dismissible onClose={() => setError(null)} variant="danger">
                    {error}
                </Alert>
            )}

            {loading ? <Spinner animation="border" /> : apps.length === 0 ? (
                <Alert variant="light">
                    No apps yet. Click
                    {' '}
                    <strong>
                        Add app
                    </strong>
                    {' '}
                    to create one.
                </Alert>
            ) : (
                <div className="d-flex flex-column gap-3">
                    {apps.map(app => (
                        <AppCard
                            app={app}
                            busy={busy}
                            key={app.id}
                            onAddEnv={() => setEditingEnv(blankEnv(app.id))}
                            onDeleteApp={() => removeApp(app)}
                            onDeleteEnv={env => removeEnv(env, app.id)}
                            onEditApp={() => setEditingApp(app)}
                            onEditEnv={env => setEditingEnv({ ...env, app_id: app.id })}
                        />
                    ))}
                </div>
            )}

            <AppEditModal
                app={editingApp}
                busy={busy}
                onClose={() => setEditingApp(null)}
                onSave={saveApp}
            />

            <EnvEditModal
                apps={apps}
                busy={busy}
                env={editingEnv}
                onClose={() => setEditingEnv(null)}
                onSave={saveEnv}
            />

        </Container>

    );

};

const AppCard = ({ app, busy, onAddEnv, onDeleteApp, onDeleteEnv, onEditApp, onEditEnv }) => (
    <Card className="border-0 shadow-sm">
        <Card.Body>

            <div className="d-flex align-items-start justify-content-between mb-2">
                <div>
                    <h5 className="m-0">
                        {app.name}
                    </h5>
                    <div className="text-muted small mt-1">
                        Context:
                        {' '}
                        {app.context ? `${app.context.length.toLocaleString()} chars` : (
                            <span className="text-warning">
                                none
                            </span>
)}
                        {' · '}
                        Sitemap:
                        {' '}
                        {app.sitemap ? `${app.sitemap.length.toLocaleString()} chars` : (
                            <span className="text-warning">
                                none
                            </span>
)}
                        {app.testrail_project_id && (
                            <>
                                {' · '}
                                TestRail project:
                                {' '}
                                <code>
                                    P
                                    {app.testrail_project_id}
                                </code>
                            </>
                        )}
                    </div>
                </div>
                <div>
                    <Button className="me-2" onClick={onEditApp} size="sm" variant="outline-secondary">
                        <FaPenToSquare className="me-1" />
                        {' '}
                        Edit primer
                    </Button>
                    <Button disabled={busy} onClick={onDeleteApp} size="sm" variant="outline-danger">
                        <FaTrashCan />
                    </Button>
                </div>
            </div>

            <div className="d-flex align-items-center gap-2 mt-3 mb-2">
                <Badge bg="secondary">
                    {(app.environments || []).length}
                    {' '}
                    environment
                    {(app.environments || []).length === 1 ? '' : 's'}
                </Badge>
                <Button onClick={onAddEnv} size="sm" variant="outline-primary">
                    <FaPlus className="me-1" />
                    {' '}
                    Add environment
                </Button>
            </div>

            {(app.environments || []).length === 0 ? (
                <div className="text-muted small">
                    No environments yet.
                </div>
            ) : (
                <Table responsive className="mb-0" size="sm">
                    <thead>
                        <tr>
                            <th>
                                Name
                            </th>
                            <th>
                                URL
                            </th>
                            <th className="text-end">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {(app.environments || []).map(env => (
                            <tr key={env.id}>
                                <td className="fw-semibold">
                                    {env.name}
                                </td>
                                <td className="text-muted small">
                                    <code>
                                        {env.url}
                                    </code>
                                </td>
                                <td className="text-end">
                                    <Button className="me-2" onClick={() => onEditEnv(env)} size="sm" variant="outline-secondary">
                                        Edit
                                    </Button>
                                    <Button disabled={busy} onClick={() => onDeleteEnv(env)} size="sm" variant="outline-danger">
                                        <FaTrashCan />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

        </Card.Body>
    </Card>
);

const AppEditModal = ({ app, busy, onClose, onSave }) => {

    const { api } = useApp();
    const [form, setForm] = useState(app || blankApp);
    const [validated, setValidated] = useState(false);
    const [trProjects, setTrProjects] = useState([]);
    const [trError, setTrError] = useState(null);

    useEffect(() => {

        setForm(app || blankApp); setValidated(false);

    }, [app]);

    useEffect(() => {

        if (!app) return;
        api.get('/api/testrail/projects')
            .then(d => setTrProjects(Array.isArray(d) ? d : (d?.projects || [])))
            .catch(e => setTrError(e?.err || e?.message || String(e)));

    }, [api, app]);

    if (!app) return null;

    const submit = e => {

        e.preventDefault();
        setValidated(true);
        if (!form.name.trim()) return;
        onSave(form);

    };

    const isNew = !form.id;

    return (
        <Modal centered scrollable show onHide={onClose} size="lg">
            <Form noValidate onSubmit={submit} validated={validated}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {isNew ? 'Add app' : `Edit ${app.name}`}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3" controlId="app-name">
                        <Form.Label>
                            Name
                        </Form.Label>
                        <Form.Control
                            required
                            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                            placeholder="LMS Student"
                            value={form.name}
                        />
                        <Form.Control.Feedback type="invalid">
                            Name is required.
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="app-testrail-project">
                        <Form.Label>
                            TestRail project
                            <span className="text-muted small ms-2">
                                (optional — links this app to a TestRail project so runs from it default to this app&apos;s environments)
                            </span>
                        </Form.Label>
                        <Form.Select
                            onChange={e => setForm(f => ({
                                ...f,
                                testrail_project_id: e.target.value ? parseInt(e.target.value, 10) : null
                            }))}
                            value={form.testrail_project_id || ''}
                        >
                            <option value="">
                                — not linked —
                            </option>
                            {trProjects.map(p => (
                                <option key={p.id} value={p.id}>
                                    {p.name}
                                    {' (P'}
                                    {p.id}
                                    )
                                </option>
                            ))}
                        </Form.Select>
                        {trError && (
                            <Form.Text className="text-danger">
                                {trError}
                            </Form.Text>
                        )}
                    </Form.Group>

                    <Tabs className="mb-3" defaultActiveKey="context">
                        <Tab eventKey="context" title="Context primer">
                            <Form.Group controlId="app-context">
                                <Form.Text className="d-block mb-2 text-muted">
                                    Markdown describing tech stack, auth, key entities, gotchas. Fed to the LLM at run start.
                                </Form.Text>
                                <Form.Control
                                    as="textarea"
                                    onChange={e => setForm(f => ({ ...f, context: e.target.value }))}
                                    placeholder="What does the app do? How do users sign in? What gotchas should the AI know about?"
                                    rows={18}
                                    style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}
                                    value={form.context || ''}
                                />
                                <Form.Text>
                                    {(form.context || '').length.toLocaleString()}
                                    {' '}
                                    characters
                                </Form.Text>
                            </Form.Group>
                        </Tab>
                        <Tab eventKey="sitemap" title="Sitemap">
                            <Form.Group controlId="app-sitemap">
                                <Form.Text className="d-block mb-2 text-muted">
                                    User flows / navigation hierarchy. Helps the AI when it gets lost mid-journey.
                                </Form.Text>
                                <Form.Control
                                    as="textarea"
                                    onChange={e => setForm(f => ({ ...f, sitemap: e.target.value }))}
                                    placeholder="## Authentication&#10;- /Login → ...&#10;&#10;## Main Flows&#10;- ..."
                                    rows={18}
                                    style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}
                                    value={form.sitemap || ''}
                                />
                                <Form.Text>
                                    {(form.sitemap || '').length.toLocaleString()}
                                    {' '}
                                    characters
                                </Form.Text>
                            </Form.Group>
                        </Tab>
                    </Tabs>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={onClose} variant="outline-secondary">
                        Cancel
                    </Button>
                    <Button disabled={busy} type="submit" variant="primary">
                        {busy ? 'Saving…' : (isNew ? 'Create' : 'Save changes')}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );

};

const EnvEditModal = ({ apps, busy, env, onClose, onSave }) => {

    const [form, setForm] = useState(env || blankEnv());
    const [validated, setValidated] = useState(false);

    useEffect(() => {

        setForm(env || blankEnv()); setValidated(false);

    }, [env]);

    if (!env) return null;

    const submit = e => {

        e.preventDefault();
        setValidated(true);
        if (!form.name.trim() || !form.url.trim() || !form.app_id) return;
        onSave(form);

    };

    const isNew = !form.id;

    return (
        <Modal centered show onHide={onClose}>
            <Form noValidate onSubmit={submit} validated={validated}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {isNew ? 'Add environment' : `Edit ${env.name}`}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row className="g-3">
                        <Col md={12}>
                            <Form.Group controlId="env-app">
                                <Form.Label>
                                    App
                                </Form.Label>
                                <Form.Select
                                    required
                                    onChange={e => setForm(f => ({ ...f, app_id: parseInt(e.target.value, 10) || null }))}
                                    value={form.app_id || ''}
                                >
                                    <option value="">
                                        Select an app…
                                    </option>
                                    {apps.map(a => (
                                        <option key={a.id} value={a.id}>
                                            {a.name}
                                        </option>
                                    ))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    Choose an app.
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={5}>
                            <Form.Group controlId="env-name">
                                <Form.Label>
                                    Name
                                </Form.Label>
                                <Form.Control
                                    required
                                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                    placeholder="Dev"
                                    value={form.name}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={7}>
                            <Form.Group controlId="env-url">
                                <Form.Label>
                                    URL
                                </Form.Label>
                                <Form.Control
                                    required
                                    onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
                                    placeholder="https://dev.gmetrix.net"
                                    type="url"
                                    value={form.url}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={onClose} variant="outline-secondary">
                        Cancel
                    </Button>
                    <Button disabled={busy} type="submit" variant="primary">
                        {busy ? 'Saving…' : (isNew ? 'Create' : 'Save changes')}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );

};
